import {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import cc from 'classcat';
import { normalizeStake } from 'utils/betslip/betslip';
import { sliceLength } from 'utils/constants/app/magic-numbers-app';
import { ColSizes, EIconSize } from 'utils/constants/app/ui';
import { removeComma } from 'utils/helpers/validators';
import { PredefinedStakes } from 'elements/Betslip/components/PredefinedStakes';
import { Col } from 'newcomponents/UI/Col';
import { Row } from 'newcomponents/UI/Row';
import { GlobalIcon } from '../GlobalIcon';
import './index.less';

type Props = {
  onChange: (value: string) => void;
  value: string | number;
  maxLength?: number;
  size?: 'small' | 'middle';
  onDestroy?: () => void;
  selected?: string | number;
  onCloseKeyboard?: Function;
  toggleQuickBet?: Function;
  predefinedStakesDisabled?: boolean;
  scrollIntoView?: boolean;
  predefinedStakesFree?: boolean;
  advanced?: boolean;
  style?: CSSProperties;
  fieldName?: string;
  disabledDot?: boolean;
  allowZero?: boolean;
};

const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

const CUSTOM_KEYBOARD_MAX_LENGTH = 11;
const CUSTOM_KEYBOASRD_MID_SIZE_FIRST_ELEMENT_ORDER = 11;
export const CustomKeyboard = memo(
  ({
    value,
    onChange,
    maxLength = CUSTOM_KEYBOARD_MAX_LENGTH,
    size = 'middle',
    onDestroy,
    selected,
    toggleQuickBet,
    onCloseKeyboard,
    predefinedStakesDisabled,
    scrollIntoView,
    predefinedStakesFree,
    advanced,
    style,
    disabledDot,
    allowZero
  }: Props) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const middleSize = useMemo(() => size === 'middle', [size]);
    const { t } = useTranslation();

    const onInput = (key: string, predefinedValue: string) => {
      let stake = '';

      if (key) {
        stake = normalizeStake(
          removeComma(value.toString()),
          key,
          maxLength,
          allowZero
        );
      } else {
        stake = normalizeStake(predefinedValue.toString(), '', 1, allowZero);
      }

      onChange(removeComma(stake));
    };

    const onBackspase = () => {
      if (value) {
        onChange(
          removeComma(value.toString()).slice(0, sliceLength.SLICE_END_MINUS_1)
        );
      }
    };

    const doneButtonHandler = useCallback(() => {
      if (toggleQuickBet) {
        toggleQuickBet(false);
      }

      onCloseKeyboard?.(false);
      onDestroy?.();
    }, [toggleQuickBet, onCloseKeyboard, onDestroy]);

    useEffect(() => {
      return () => {
        onDestroy?.();
      };
    }, []);

    useEffect(() => {
      if (scrollIntoView) {
        ref?.current?.scrollIntoView({ block: 'center' });
      }
    }, [selected]);

    return (
      <div className="keyboard__container" ref={ref} style={style}>
        <Row>
          <Col
            span={
              middleSize && !predefinedStakesFree
                ? ColSizes.SIZE_17
                : ColSizes.SIZE_24
            }
          >
            <div className="keyboard__keys">
              <div className="keyboard__numberKeys">
                {keys.map((key, index) => (
                  <div
                    data-testid={key}
                    className={cc([
                      'keyboard__key',
                      {
                        'keyboard__key--grow': key === '0',
                        'keyboard__key--size': size === 'small'
                      }
                    ])}
                    key={key}
                    onClick={() => onInput(key, '')}
                    style={{
                      order:
                        key === '0' && size === 'middle'
                          ? CUSTOM_KEYBOASRD_MID_SIZE_FIRST_ELEMENT_ORDER
                          : index
                    }}
                  >
                    {key}
                  </div>
                ))}
                <div
                  data-testid="."
                  className={cc([
                    'keyboard__key',
                    { 'keyboard__key--size': size === 'small' }
                  ])}
                  style={{ order: 10 }}
                  onClick={() => (disabledDot ? () => {} : onInput('.', ''))}
                  aria-disabled={disabledDot}
                >
                  <GlobalIcon
                    lib="generic"
                    name="dot"
                    theme="default"
                    size={EIconSize._4}
                  />
                </div>
                <div
                  data-testid="backspace"
                  className={cc([
                    'keyboard__key keyboard__key--grow',
                    { 'keyboard__key--size': size === 'small' }
                  ])}
                  style={{ order: 12 }}
                  onClick={onBackspase}
                >
                  <GlobalIcon
                    lib="generic"
                    name="backspace"
                    theme="default"
                    size={EIconSize._16}
                  />
                </div>
                {advanced && (
                  <div
                    data-testid="keyboard-done"
                    className={cc([
                      'keyboard__key keyboard__key--grow keyboard__key--done',
                      { 'keyboard__key--size': size === 'small' }
                    ])}
                    style={{ order: 13 }}
                    onClick={doneButtonHandler}
                  >
                    <span>{t('sportsbook.done')}</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
          {middleSize && (
            <Col
              span={predefinedStakesFree ? ColSizes.SIZE_24 : ColSizes.SIZE_7}
            >
              {!predefinedStakesFree && (
                <PredefinedStakes
                  onChange={predefinedValue => onInput('', predefinedValue)}
                  disabled={predefinedStakesDisabled}
                  currentStake={value}
                />
              )}
              <div
                data-testid="keyboard-done"
                className={cc([
                  'keyboard__key keyboard__key--grow keyboard__key--done',
                  { 'keyboard__key--size': size === 'small' }
                ])}
                onClick={doneButtonHandler}
              >
                <span>{t('sportsbook.done')}</span>
              </div>
            </Col>
          )}
        </Row>
      </div>
    );
  }
);
